import { useMemo } from 'react'
import { useSiteData } from '../context/DataContext.jsx'
import RailBlock from '../components/RailBlock.jsx'
import ErrorFallback from '../components/ErrorFallback.jsx'
import Skeleton from '../components/Skeleton.jsx'

const CATEGORY_ORDER = [
  'language',
  'ai_ml',
  'web',
  'data',
  'edtech',
  'infra',
  'tooling',
]

const CATEGORY_LABELS = {
  language: 'LANGUAGES',
  ai_ml: 'AI / ML',
  web: 'WEB',
  data: 'DATA',
  edtech: 'EDTECH',
  infra: 'INFRA',
  tooling: 'TOOLING',
}

const CATEGORY_NOTES = {
  language: '',
  ai_ml: '',
  web: '',
  data: '',
  edtech: '',
  infra: '',
  tooling: '',
}

function DotMeter({ comfort }) {
  return (
    <div className="flex gap-1">
      {Array.from({ length: 5 }).map((_, i) => (
        <div
          key={i}
          className={`w-2 h-2 rounded-sm ${
            i < comfort ? 'bg-soft-ink' : 'bg-soft-rule'
          }`}
        />
      ))}
    </div>
  )
}

function SkillsPage() {
  const { data: siteData, loading, error } = useSiteData()
  const data = siteData?.skills || []

  const grouped = useMemo(() => {
    if (!data) return {}
    const map = {}
    for (const skill of data) {
      if (!map[skill.category]) map[skill.category] = []
      map[skill.category].push(skill)
    }
    for (const cat of Object.keys(map)) {
      map[cat].sort((a, b) => b.comfort - a.comfort || a.name.localeCompare(b.name))
    }
    return map
  }, [data])

  if (loading) {
    return (
      <div className="pg-layout">
        <div className="space-y-6">
          <Skeleton className="h-4 w-32 mb-2" />
          <Skeleton className="h-14 w-96 mb-2" />
          <Skeleton className="h-5 w-80 mb-10" />
          {[1, 2, 3].map((i) => (
            <div key={i} className="border-t border-soft-rule py-6">
              <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-4 sm:gap-7">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-full" />
              </div>
            </div>
          ))}
        </div>
        <div className="space-y-4">
          <Skeleton className="h-56 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    )
  }

  if (error) return <ErrorFallback message="Failed to load skills" onRetry={refetch} />

  return (
    <div className="pg-layout">
      {/* LEFT */}
      <div>
        <p className="font-mono text-[11px] uppercase tracking-[0.6px] text-soft-faint mb-3">
          Stack · seven groups
        </p>
        <h1 className="text-[36px] sm:text-[48px] md:text-[56px] font-normal leading-[1.02] tracking-[-1.8px] mb-4">
          What I reach for, grouped by where it fits.
        </h1>
        <p className="text-[18px] leading-[1.5] text-soft-dim max-w-[620px] mb-9">
          The skills are grouped by domain. The comfort scale on the right tells you which ones
          I have used in production.
        </p>

        <div>
          {CATEGORY_ORDER.map((cat, index) => {
            const skills = grouped[cat] || []
            return (
              <div
                key={cat}
                className={`grid grid-cols-[160px_1fr] gap-7 pt-[22px] pb-6 ${
                  index > 0 ? 'border-t border-soft-rule' : 'border-t border-soft-rule'
                }`}
              >
                <span className="font-mono text-[12px] uppercase tracking-[0.6px] text-soft-faint self-baseline">
                  {CATEGORY_LABELS[cat]}
                </span>
                <div>
                  <p className="text-[18px] leading-[1.45] text-soft-ink">
                    {skills.map((s) => s.name).join(' · ')}
                  </p>
                  {CATEGORY_NOTES[cat] && (
                    <p className="text-[14px] leading-[1.5] text-soft-dim mt-2">
                      {CATEGORY_NOTES[cat]}
                    </p>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* RIGHT rail */}
      <aside>
        <RailBlock eyebrow="Comfort scale" top>
          <div className="divide-y divide-soft-rule">
            {Object.values(grouped)
              .flat()
              .sort((a, b) => b.comfort - a.comfort || a.name.localeCompare(b.name))
              .slice(0, 8)
              .map((skill) => (
                <div
                  key={skill.id}
                  className="flex justify-between items-center py-2.5"
                  aria-label={`${skill.name}: comfort ${skill.comfort} of 5`}
                >
                  <span className="text-[13.5px] text-soft-ink">{skill.name}</span>
                  <DotMeter comfort={skill.comfort} />
                </div>
              ))}
          </div>
          <p className="font-mono text-[11px] text-soft-faint mt-3 pt-3 border-t border-soft-rule">
            5 = SHIP TODAY · 1 = HAVE TOUCHED
          </p>
        </RailBlock>

        {/*<RailBlock eyebrow="Recent reading">*/}
        {/*  <div className="divide-y divide-soft-rule">*/}
        {/*    {[*/}
        {/*      { title: 'Designing Data-Intensive Applications', author: 'Martin Kleppmann' },*/}
        {/*      { title: 'Building Machine Learning Pipelines', author: 'Hannes Hapke & Catherine Nelson' },*/}
        {/*      { title: "The Manager's Path", author: 'Camille Fournier' },*/}
        {/*    ].map((book) => (*/}
        {/*      <div key={book.title} className="py-3 first:pt-0 last:pb-0">*/}
        {/*        <p className="text-[13.5px] leading-[1.4] text-soft-ink">{book.title}</p>*/}
        {/*        <p className="font-mono text-[10.5px] tracking-[0.3px] text-soft-faint mt-[3px]">*/}
        {/*          {book.author}*/}
        {/*        </p>*/}
        {/*      </div>*/}
        {/*    ))}*/}
        {/*  </div>*/}
        {/*</RailBlock>*/}
      </aside>
    </div>
  )
}

export default SkillsPage
